import { useMemo } from "react"
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/cn"
import type { CategoryBucket } from "@/types/analytics_escalations"

// Donut chart of escalations grouped by reason. Click a slice (or the
// matching list row) to filter the detail table; clicking the same key
// again clears the filter.

type Props = {
  buckets: CategoryBucket[]
  selectedKey: string | null
  onSelect?: (reasonKey: string | null) => void
}

// Tailwind color tokens cycled for the donut + legend dots. Using the
// existing palette only (brand-red / vip-gold / status-* / sky / violet
// extras pulled from default Tailwind). bg-* classes for swatches; the
// donut itself needs hex/computed for SVG fill, so we ship a parallel
// hex array — derived once from a CSS var lookup at render time.
const PALETTE = [
  { swatch: "bg-brand-red",       fill: "#E0455B" },   // brand-red
  { swatch: "bg-vip-gold",        fill: "#D4A24C" },   // vip-gold
  { swatch: "bg-status-success",  fill: "#22c55e" },
  { swatch: "bg-status-warning",  fill: "#eab308" },
  { swatch: "bg-status-error",    fill: "#ef4444" },
  { swatch: "bg-sky-500",         fill: "#0ea5e9" },
  { swatch: "bg-violet-500",      fill: "#8b5cf6" },
  { swatch: "bg-slate-500",       fill: "#64748b" },
]

export function EscalationCategoryDonut({ buckets, selectedKey, onSelect }: Props) {
  const total = useMemo(() => buckets.reduce((acc, b) => acc + b.count, 0), [buckets])

  const handleSelect = (key: string) => {
    if (!onSelect) return
    onSelect(selectedKey === key ? null : key)
  }

  if (buckets.length === 0) {
    return (
      <Card className="p-5">
        <h2 className="mb-2 text-lg font-semibold">By category</h2>
        <p className="text-sm text-app-muted">No escalations in window.</p>
      </Card>
    )
  }

  return (
    <Card className="p-5">
      <h2 className="mb-4 text-lg font-semibold">By category</h2>
      <div className="grid items-center gap-4 sm:grid-cols-[180px_minmax(0,1fr)]">
        <div className="relative h-[200px] min-w-0">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Tooltip
                contentStyle={{ background: "#1f1f23", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#aaa" }}
                itemStyle={{ color: "#fff" }}
              />
              <Pie
                data={buckets}
                dataKey="count"
                nameKey="name"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={1}
                stroke="none"
                isAnimationActive={false}
                onClick={(slice) => {
                  // Recharts forwards the underlying data row as the first arg.
                  const k = (slice as { key?: unknown })?.key
                  if (typeof k === "string") handleSelect(k)
                }}
              >
                {buckets.map((b, idx) => {
                  const isSel = selectedKey !== null && selectedKey !== b.key
                  return (
                    <Cell
                      key={b.key}
                      fill={PALETTE[idx % PALETTE.length].fill}
                      style={{ cursor: onSelect ? "pointer" : "default", opacity: isSel ? 0.35 : 1 }}
                    />
                  )
                })}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-2xl font-semibold tracking-tight text-app-text">
              {total.toLocaleString()}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-app-muted">total</div>
          </div>
        </div>

        <ul className="space-y-1.5">
          {buckets.map((b, idx) => {
            const swatch = PALETTE[idx % PALETTE.length].swatch
            const active = selectedKey === b.key
            const dim = selectedKey !== null && !active
            return (
              <li key={b.key}>
                <button
                  type="button"
                  onClick={() => handleSelect(b.key)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-sm transition",
                    active ? "bg-white/10" : "hover:bg-white/5",
                    dim && "opacity-60",
                  )}
                >
                  <span className={cn("h-2.5 w-2.5 flex-none rounded-full", swatch)} />
                  <span className="flex-1 truncate text-app-text">{b.name}</span>
                  <span className="text-app-muted tabular-nums">{b.count.toLocaleString()}</span>
                  <span className="w-12 text-right text-app-subtle tabular-nums">{b.pct}%</span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </Card>
  )
}
